import React from 'react';
import { getIcon, accent } from '../helpers';

/**
 * Template "grid" — trois cartes, une icône chacune.
 *
 * `item.ton` (optionnel) : "cyan" | "amber" | "violet". Reprend la couleur d'un
 * des trois cas de « Preuve terrain ». Une seule slide s'en sert — « Où
 * chercher le vôtre » — et c'est sa raison d'être : les trois couleurs que la
 * salle a suivies pendant vingt minutes se retrouvent enfin dans le même écran,
 * une par carte. La couleur n'illustre pas la démonstration, elle EST la
 * démonstration : trois teintes, trois endroits, trois goulots.
 *
 * ⚠️ L'ordre des cartes doit rester l'ordre des cas (PULSE, l'ERP, la forge).
 * Trié autrement, la salle ne fait plus le rapprochement et il ne reste que
 * trois cartes bariolées.
 *
 * `item.valeur` + `item.valeurTon` (optionnels) : un chiffre posé à côté du
 * titre, en vert s'il monte ("gain"), en rouge s'il descend ("perte"). Une
 * seule slide s'en sert — « Ce que l'IA change dans la machine » — et elle en a
 * besoin : son sujet n'est pas la valeur de chaque facteur, c'est le fait que
 * DEUX MONTENT ET UN DESCEND. Dans la chaîne du titre, ce fait se lisait ; posé
 * en couleur, il se voit. Les deux tons ne se cumulent pas avec `item.ton` (les
 * cartes de couleur de cas n'ont pas de valeur), donc aucune slide existante ne
 * bouge.
 *
 * `item.preuve` (optionnel) : une seconde ligne sous `desc`, plus petite et
 * plus sourde. Elle sépare deux choses qui ne s'adressent pas à la même
 * personne : `desc` porte la consigne — ce que la salle peut appliquer chez
 * elle — et `preuve` porte le cas d'Adrien, qui la rend crédible. Sur « Où
 * chercher le vôtre », c'est là que vivent les chiffres du talk.
 *
 * ⚠️ Ne pas y mettre autre chose qu'une preuve. Une carte dont les deux lignes
 * disent la même chose à deux tailles différentes lit moins bien qu'une carte
 * à une ligne.
 *
 * `slide.complement` (optionnel) : une ligne sous la grille, en petit. Ce n'est
 * pas une quatrième carte et ça ne doit pas le devenir — c'est la place des
 * chiffres qu'on ne peut pas taire sans se rendre attaquable, mais qui ne sont
 * pas le sujet.
 */
const GridSlide = ({ slide, slideKey, currentSlide, slidesLength }) => {
    return (
        <div key={slideKey} className="h-full p-12 flex flex-col justify-center relative">
            <div className="relative z-10">
                <h2 className="text-6xl pb-2 font-bold mb-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up text-center">
                    {slide.title}
                </h2>
                <p className={`text-2xl text-blue-200/60 animate-fade-in-up stagger-1 text-center ${slide.complement ? 'mb-8' : 'mb-12'}`}>
                    {slide.subtitle}
                </p>

                <div className="grid grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {slide.items.map((item, i) => {
                        const ton = item.ton ? accent(item.ton) : null;
                        return (
                            <div key={i} className={`bg-gradient-to-br backdrop-blur-sm border rounded-2xl p-8 transition-all group animate-fade-in-scale ${ton
                                ? `${ton.halo} ${ton.bord}`
                                : 'from-blue-500/10 to-purple-500/10 border-blue-500/20 hover:border-blue-500/40 hover:from-blue-500/15 hover:to-purple-500/15'}`}
                                style={{ animationDelay: `${i * 0.15}s`, opacity: 0 }}>
                                {/* L'icône ET le titre prennent la couleur : depuis le
                                    fond de la salle, une icône seule ne suffit pas à
                                    rattacher la carte au cas qu'on vient de quitter. */}
                                <div className={`text-5xl mb-4 group-hover:scale-110 transition-transform ${ton ? ton.texte : 'text-white'}`}>
                                    {getIcon(item.icon)}
                                </div>
                                <h3 className="text-3xl font-bold mb-3 flex items-baseline gap-3 flex-wrap">
                                    <span className={ton ? ton.texte : 'text-white'}>{item.title}</span>
                                    {item.valeur && (
                                        <span className={`font-black ${item.valeurTon === 'perte'
                                            ? 'text-rose-400' : 'text-emerald-400'}`}>
                                            {item.valeur}
                                        </span>
                                    )}
                                </h3>
                                <p className="text-slate-300 text-xl leading-relaxed">{item.desc}</p>
                                {/* La preuve est en retrait VOLONTAIREMENT. `desc`
                                    est une consigne, adressée à la salle et
                                    applicable chez elle ; `preuve` est le cas
                                    d'Adrien, qui la crédibilise. Au même poids
                                    typographique, l'œil ne sait pas par laquelle
                                    commencer — et c'est la consigne qui leur sert. */}
                                {item.preuve && (
                                    <p className="text-slate-400 text-lg leading-relaxed mt-3">{item.preuve}</p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {slide.complement && (
                    <p className="max-w-5xl mx-auto mt-6 text-center text-lg text-slate-400 animate-fade-in-up stagger-4">
                        {slide.complement}
                    </p>
                )}
            </div>
        </div>
    );
};

export default GridSlide;
